import { LinkProps, useLocation, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  BadgeDollarSign,
  Bot,
  Building2,
  ClipboardCheck,
  ClipboardList,
  FileSearch,
  FileStack,
  FileText,
  FolderOpen,
  Gauge,
  Handshake,
  KeyRound,
  LayoutGrid,
  Mail,
  MailPlus,
  MessageCircle,
  MessageSquare,
  MessageSquareText,
  MessagesSquare,
  Network,
  PlusCircle,
  Settings,
  Shield,
  ShieldCheck,
  ShieldQuestion,
  SlidersHorizontal,
  UserCheck,
  Users,
} from "lucide-react";

import { RouteLink } from "@/components/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip/Tooltip";

type TNavItem = {
  label: string;
  /** Route target. Omitted for grouping-only rows that just nest `children`. */
  to?: LinkProps["to"];
  icon: React.ComponentType<{ className?: string }>;
  isActive?: () => boolean;
  /** Nested navigation items (e.g. Deal Network → Investor Platform). */
  children?: TNavItem[];
};

type TProps = {
  isCollapsed?: boolean;
};

export default function AdminSidebar({ isCollapsed = false }: TProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  // Organized by what the administrator manages, not by backend structure —
  // mirrors enterprise admin consoles (Google Admin, Entra, Okta, GitHub, AWS).
  const dashboardItems: TNavItem[] = [{ label: "Analytics", to: "/app/admin/analytics" as const, icon: Gauge }];

  const identityItems: TNavItem[] = [
    { label: "Users", to: "/app/admin/users" as const, icon: Users },
    { label: "Reviewers", to: "/app/admin/reviewers" as const, icon: UserCheck },
    { label: "Sales", to: "/app/admin/sales" as const, icon: BadgeDollarSign },
    { label: "Roles & Permissions", to: "/app/admin/roles" as const, icon: ShieldCheck },
    { label: "Login Attempts", to: "/app/admin/login-attempts" as const, icon: KeyRound },
    { label: "Security Audit Logs", to: "/app/admin/audit-logs" as const, icon: FileSearch },
  ];

  const organizationItems: TNavItem[] = [
    { label: "Companies", to: "/app/admin/companies" as const, icon: Building2 },
    { label: "Dashboard Sections", to: "/app/admin/sections" as const, icon: LayoutGrid },
    { label: "Onboarding Questions", to: "/app/admin/onboarding-questions" as const, icon: PlusCircle },
    { label: "Onboarding Responses & Files", to: "/app/admin/onboarding-answers" as const, icon: FileStack },
    {
      label: "Deal Network",
      icon: Network,
      children: [
        { label: "Investor Platform", to: "/app/admin/investor-platform" as const, icon: Handshake },
        { label: "Investor Onboarding Jobs", to: "/app/admin/investor-onboarding-jobs" as const, icon: UserCheck },
        { label: "Investor Invitations", to: "/app/admin/investor-invites" as const, icon: MailPlus },
      ],
    },
  ];

  const aiItems: TNavItem[] = [{ label: "AI Usage & Analytics", to: "/app/admin/ai-usage" as const, icon: Bot }];

  const communicationsItems: TNavItem[] = [
    { label: "Chat Moderation", to: "/app/admin/chat" as const, icon: MessagesSquare },
    { label: "Feedback", to: "/app/admin/feedback" as const, icon: MessageSquare },
    { label: "WhatsApp Groups", to: "/app/admin/whatsapp-groups" as const, icon: MessageCircle },
    { label: "WhatsApp Templates", to: "/app/admin/whatsapp-templates" as const, icon: MessageSquareText },
    { label: "Email Verification Logs", to: "/app/admin/verification/email-logs" as const, icon: Mail },
    { label: "WhatsApp Verification Logs", to: "/app/admin/verification/phone-logs" as const, icon: MessageSquare },
  ];

  const storageItems: TNavItem[] = [{ label: "File Management", to: "/app/admin/files" as const, icon: FolderOpen }];

  const complianceItems: TNavItem[] = [
    {
      label: "Legal Documents",
      to: "/app/admin/legal" as const,
      icon: FileText,
      isActive: () =>
        currentPath === "/app/admin/legal" ||
        (currentPath.startsWith("/app/admin/legal/") && !currentPath.startsWith("/app/admin/legal/acknowledgements")),
    },
    {
      label: "Legal Acknowledgements",
      to: "/app/admin/legal/acknowledgements" as const,
      icon: ClipboardList,
      isActive: () => currentPath.startsWith("/app/admin/legal/acknowledgements"),
    },
    { label: "Verification Settings", to: "/app/admin/verification/settings" as const, icon: SlidersHorizontal },
    { label: "GDPR Requests", to: "/app/admin/gdpr" as const, icon: ShieldQuestion },
    { label: "Compliance Audit Logs", to: "/app/admin/verification/audit-logs" as const, icon: ClipboardCheck },
  ];

  const platformItems: TNavItem[] = [
    { label: "Admin Settings", to: "/app/admin/settings" as const, icon: Shield },
    { label: "System Configuration", to: "/app/admin/system" as const, icon: Settings },
  ];

  const sections: { title: string; items: TNavItem[] }[] = [
    { title: "Dashboard", items: dashboardItems },
    { title: "Identity & Access", items: identityItems },
    { title: "Organizations", items: organizationItems },
    { title: "AI", items: aiItems },
    { title: "Communications", items: communicationsItems },
    { title: "Storage", items: storageItems },
    { title: "Compliance", items: complianceItems },
    { title: "Platform", items: platformItems },
  ];

  const isActive = (path: string) => currentPath.startsWith(path);

  const getActive = (item: TNavItem) => (item.isActive ? item.isActive() : item.to ? isActive(item.to) : false);

  // Flatten one level of nesting (grouping rows contribute their children).
  const flattenItems = (items: TNavItem[]): TNavItem[] =>
    items.flatMap((item) => [...(item.to ? [item] : []), ...(item.children ?? [])]);

  // ── Collapsed (icon-only) mode ──────────────────────────────────────────
  if (isCollapsed) {
    const allItems: TNavItem[] = flattenItems(sections.flatMap((section) => section.items));

    return (
      <div className="flex h-full w-full flex-col items-center gap-1 overflow-y-auto rounded-2xl bg-card py-3 shadow-lg">
        {allItems.map((item) => {
          const Icon = item.icon;
          const active = getActive(item);
          return (
            <Tooltip key={item.label}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => void navigate({ to: item.to })}
                  className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                    active
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                  aria-label={item.label}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          );
        })}

        <div className="mt-auto flex w-full flex-col items-center border-t border-border pt-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => void navigate({ to: "/app" })}
                className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label="Back to App"
              >
                <ArrowLeft className="h-4 w-4 flex-shrink-0" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Back to App</TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  // ── Expanded mode ───────────────────────────────────────────────────────
  const renderNavItem = (item: TNavItem) => {
    const Icon = item.icon;

    // Grouping-only row: render a label with its children nested and indented.
    if (item.children && !item.to) {
      return (
        <div
          key={item.label}
          className="space-y-1"
        >
          <div className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-muted-foreground">
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span>{item.label}</span>
          </div>
          <div className="ml-[1.4rem] space-y-1 border-l border-border pl-2">{item.children.map(renderNavItem)}</div>
        </div>
      );
    }

    const active = getActive(item);
    return (
      <RouteLink
        key={item.label}
        to={item.to}
        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
        }`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span>{item.label}</span>
      </RouteLink>
    );
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-card p-2 shadow-lg md:p-3 lg:p-4">
      <nav className="flex-1 space-y-1 overflow-y-auto">
        {sections.map((section, index) => (
          <div key={section.title}>
            {index > 0 && <div className="my-1 border-t border-border" />}
            <div className="space-y-1">
              <h3 className="px-3 text-xs font-semibold uppercase tracking-wider text-primary">{section.title}</h3>
              {section.items.map(renderNavItem)}
            </div>
          </div>
        ))}
      </nav>

      <div className="mt-auto border-t border-border pt-3">
        <RouteLink
          to="/app"
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4 flex-shrink-0" />
          <span>Back to App</span>
        </RouteLink>
      </div>
    </div>
  );
}
